import { useSession } from "@supabase/auth-helpers-react";
import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";

// EN-1414: the clinic's "Show Floating Buttons" preference. Kept at module level because
// IntercomManager mounts above AccountProvider, and because a later boot must not re-show a
// launcher the preference already hid.
let defaultLauncherHidden = false;

export const isIntercomLauncherHidden = () => defaultLauncherHidden;

export const setIntercomLauncherHidden = (hidden: boolean) => {
    defaultLauncherHidden = hidden;
    try {
        (window as any)?.Intercom?.("update", { hide_default_launcher: hidden });
    } catch (e: any) {
        Sentry.captureException(e);
    }
};

export const IntercomManager = () => {
    const pathname = window.location.pathname;
    const session = useSession();

    const accessToken = session?.access_token ?? null;
    const initializedRef = useRef(false);
    const user = session?.user;

    //Attempt to load once.
    useEffect(() => {
        // Only try to load Intercom script once
        if (!initializedRef.current) {
            try {
                const w: any = window;
                const ic = w.Intercom;
                if (typeof ic !== "function") {
                    const d = document;
                    const i: any = function () {
                        i.c(arguments);
                    };
                    i.q = [];
                    i.c = function (args: any) {
                        i.q.push(args);
                    };
                    w.Intercom = i;
                    const l = function () {
                        const s = d.createElement("script");
                        s.type = "text/javascript";
                        s.async = true;
                        s.src = "https://widget.intercom.io/widget/qd1sb79b";
                        const x: any = d.getElementsByTagName("script")[0];
                        x.parentNode.insertBefore(s, x);
                    };
                    if (document.readyState === "complete") {
                        l();
                    } else if (w.attachEvent) {
                        w.attachEvent("onload", l);
                    } else {
                        w.addEventListener("load", l, false);
                    }
                }
                initializedRef.current = true;
            } catch (e: any) {
                Sentry.captureException(e);
            }
        }
    }, []);
    //Set User.
    useEffect(() => {
        try {
            if (accessToken && initializedRef.current) {
                // Call boot method
                (window as any)?.Intercom("boot", {
                    app_id: "qd1sb79b",
                    custom_launcher_selector: "#IntercomCustomLink",
                    hide_default_launcher: defaultLauncherHidden,
                    email: user?.email ?? "Unknown User",
                });
            } else if (!accessToken && initializedRef.current) {
                // Shutdown Intercom if user is not authenticated and Intercom is initialized
                (window as any)?.Intercom("shutdown");
                // EN-1414: don't carry this clinic's hidden-launcher choice into the next session's
                // boot. Account switches within a session are re-applied by PermissionWrapper.
                defaultLauncherHidden = false;
            }
        } catch (e: any) {
            Sentry.captureException(e);
        }
    }, [accessToken, user]);

    // Update Intercom on route changes
    useEffect(() => {
        try {
            const handleRouteChange = () => {
                if (accessToken) {
                    (window as any)?.Intercom("update");
                }
            };
            handleRouteChange();
        } catch (e: any) {
            Sentry.captureException(e);
        }
    }, [pathname, accessToken]);

    return null; // This component doesn't render anything itself
};

// EN-893: removed the unreferenced `IntercomCustomLink` export. Its label read
// "👋 24/7 Chat Support", but chat is staffed Mon–Fri 9am–4pm MT (best-effort after
// hours) — exactly the expectation this work exists to correct. The launcher is still
// bound by id to the Help Center row in ProfileMenu via custom_launcher_selector.
